import type { TriageAnswerRecord, TriageAnswerValue, TriageQuestion, TriageQuestionnaire } from '@core/entities/Triage';
import { noneOfTheAboveAnswerCode } from '@core/entities/Triage';

const patientSexLinkId = 'sex-at-birth';

export interface PreparePreTriageSubmissionAnswersRequest {
  questionnaire: TriageQuestionnaire;
  answers: TriageAnswerRecord;
}

export interface PreparePreTriageSubmissionAnswersResult {
  normalizedAnswers: TriageAnswerRecord;
  submissionAnswers: TriageAnswerRecord;
  patientSex: 'male' | 'female' | undefined;
}

export class PreparePreTriageSubmissionAnswersUseCase {
  public execute(request: PreparePreTriageSubmissionAnswersRequest): PreparePreTriageSubmissionAnswersResult {
    const questions = this.flattenQuestions(request.questionnaire.questions);
    const knownLinkIds = new Set(questions.map((question) => question.linkId));

    const normalizedAnswers = this.filterKnownAnswers(request.answers, knownLinkIds);
    for (const question of questions) {
      this.normalizeQuestionAnswer(normalizedAnswers, question);
    }

    return {
      normalizedAnswers,
      submissionAnswers: this.omitUiOnlyAnswers(normalizedAnswers),
      patientSex: this.getPatientSexAnswer(normalizedAnswers),
    };
  }

  private filterKnownAnswers(
    answers: TriageAnswerRecord,
    knownLinkIds: ReadonlySet<string>,
  ): Record<string, TriageAnswerValue> {
    return Object.fromEntries(
      Object.entries(answers).filter(([linkId]) =>
        knownLinkIds.has(linkId) || linkId === patientSexLinkId,
      ),
    );
  }

  private normalizeQuestionAnswer(answers: Record<string, TriageAnswerValue>, question: TriageQuestion): void {
    if (!question.options) {
      return;
    }

    const rawAnswer = answers[question.linkId];
    if (rawAnswer === undefined) {
      return;
    }

    const allowedCodes = new Set([...question.options.map((option) => option.code), noneOfTheAboveAnswerCode]);
    if (Array.isArray(rawAnswer)) {
      this.setNormalizedArrayAnswer(answers, question.linkId, rawAnswer, allowedCodes);
      return;
    }

    if (typeof rawAnswer === 'string' && !allowedCodes.has(rawAnswer)) {
      delete answers[question.linkId];
    }
  }

  private setNormalizedArrayAnswer(
    answers: Record<string, TriageAnswerValue>,
    linkId: string,
    rawAnswer: readonly string[],
    allowedCodes: ReadonlySet<string>,
  ): void {
    const filtered = rawAnswer.filter((value) => allowedCodes.has(value));
    if (filtered.length === 0) {
      delete answers[linkId];
      return;
    }

    answers[linkId] = filtered;
  }

  private omitUiOnlyAnswers(answers: TriageAnswerRecord): TriageAnswerRecord {
    return Object.fromEntries(
      Object.entries(answers)
        .map(([linkId, value]) => [linkId, Array.isArray(value) ? value.filter((code) => code !== noneOfTheAboveAnswerCode) : value] as const)
        .filter(([, value]) => !Array.isArray(value) || value.length > 0),
    );
  }

  private getPatientSexAnswer(answers: TriageAnswerRecord): 'male' | 'female' | undefined {
    const answer = answers[patientSexLinkId];
    return answer === 'male' || answer === 'female'
      ? answer
      : undefined;
  }

  private flattenQuestions(questions: readonly TriageQuestion[]): readonly TriageQuestion[] {
    return questions.flatMap((question) => [
      question,
      ...this.flattenQuestions(question.children ?? []),
    ]);
  }
}
